import React, { useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const AnimalPhotoUploadModal = ({ open, onClose, onUpload, animalName, t }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [error, setError] = useState(null);
    const [uploading, setUploading] = useState(false);

    const validateFile = (file) => {
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const maxSize = 5 * 1024 * 1024; // 5 MB

        if (!allowedTypes.includes(file.type)) {
            return t('invalidFileType');
        }
        if (file.size > maxSize) {
            return t('fileTooLarge');
        }
        return null;
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const validationError = validateFile(file);
            if (validationError) {
                setError(validationError);
                setSelectedFile(null);
                setPreviewUrl(null);
            } else {
                setError(null);
                setSelectedFile(file);
                setPreviewUrl(URL.createObjectURL(file));
            }
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setUploading(true);
        setError(null);
        try {
            await onUpload(selectedFile);
            handleClose();
        } catch (err) {
            setError(t('uploadFailed'));
            setUploading(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
        setError(null);
        setUploading(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">{t('photoOf', { name: animalName })}</Typography>
                    <IconButton onClick={handleClose} disabled={uploading}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        py: 2,
                    }}
                >
                    {previewUrl ? (
                        <Box
                            component="img"
                            src={previewUrl}
                            alt="Preview"
                            sx={{
                                width: '100%',
                                aspectRatio: '1/1',
                                objectFit: 'cover',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                aspectRatio: '1/1',
                                borderRadius: 2,
                                bgcolor: 'grey.100',
                                border: '2px dashed',
                                borderColor: 'grey.400',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'grey.200' },
                            }}
                            component="label"
                        >
                            <input
                                type="file"
                                hidden
                                accept="image/jpeg,image/png,image/webp"
                                onChange={handleFileChange}
                                data-testid="animal-photo-input"
                            />
                            <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                {t('clickToUpload')}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ width: '100%', textAlign: 'center' }}>
                        <Button
                            variant="outlined"
                            component="label"
                            disabled={uploading}
                            size="small"
                        >
                            {previewUrl ? t('chooseAnotherFile') : t('chooseFile')}
                            <input
                                type="file"
                                hidden
                                accept="image/jpeg,image/png,image/webp"
                                onChange={handleFileChange}
                            />
                        </Button>
                        <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                            JPG/PNG • max. 5 MB
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ width: '100%' }}>
                            {error}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={uploading} data-testid="cancel-upload-button">
                    {t('cancel')}
                </Button>
                <Button
                    onClick={handleUpload}
                    variant="contained"
                    disabled={!selectedFile || uploading}
                    startIcon={uploading ? <CircularProgress size={20} /> : null}
                    data-testid="confirm-upload-button"
                >
                    {uploading ? t('uploading') : t('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AnimalPhotoUploadModal;
